/**
 * Voice-message recorder.
 *
 * Wraps MediaRecorder + getUserMedia in a small subscribable state
 * machine so the composer can show a recording HUD without owning the
 * raw browser APIs.
 *
 * Lifecycle:
 *   idle
 *     → start() opens the mic prompt, creates a MediaRecorder
 *     → state becomes 'recording' with a millisecond timer
 *   recording
 *     → stop() finalises the blob, returns { blob, mime, durationMs }
 *     → cancel() throws the blob away, releases the mic
 *   error
 *     → mic-denied / unsupported / quota — UI surfaces a toast
 *
 * Why a singleton + pub/sub instead of a hook:
 *   - Push-to-talk button + composer-level cancel button + a future
 *     mini-player all need to read the same state.
 *   - The recording must survive a re-render of the composer (channel
 *     switch mid-record is rare but should not silently drop the take).
 */

import { getVoicePreferences } from '@/lib/voicePreferences';
import { buildProcessedAudio, type ProcessedAudio } from '@/lib/voice/audioGraph';
import i18n from '@/i18n';

export interface RecorderState {
  kind: 'idle' | 'recording' | 'error';
  startedAt?: number;
  durationMs?: number;
  /** Human-readable Russian reason, set when kind === 'error'. */
  error?: string;
}

export interface RecordingResult {
  blob: Blob;
  mime: string;
  durationMs: number;
}

type Listener = (state: RecorderState) => void;

let current: RecorderState = { kind: 'idle' };
const listeners = new Set<Listener>();
let recorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let stream: MediaStream | null = null;
// Our DSP/RNNoise processing graph for the recording (null = raw mic).
let audioGraph: ProcessedAudio | null = null;
let tickHandle: ReturnType<typeof setInterval> | null = null;
let startedAt = 0;
let chosenMime = 'audio/webm';

function set(next: RecorderState) {
  current = next;
  for (const fn of listeners) fn(next);
}

export function getRecorder(): RecorderState {
  return current;
}

export function subscribeRecorder(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}

export function isRecorderSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

/** Pick the highest-quality mime the browser supports. */
function pickMime(): string {
  if (typeof MediaRecorder === 'undefined') return 'audio/webm';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return 'audio/webm';
}

export async function startRecording(): Promise<void> {
  if (!isRecorderSupported()) {
    set({ kind: 'error', error: i18n.t('lib2.recorder.unsupported') });
    return;
  }
  if (current.kind === 'recording') return;
  const prefs = getVoicePreferences();
  try {
    // Browser AEC stays driven by the user's echo-cancellation pref; our own
    // DSP/RNNoise stages do the noise work, so disable the browser's NS/AGC
    // (matches the live-voice capture path in voicePreferences).
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: prefs.echoCancellation,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
  } catch (err) {
    const denied =
      err instanceof DOMException &&
      (err.name === 'NotAllowedError' || err.name === 'SecurityError');
    set({
      kind: 'error',
      error: denied
        ? i18n.t('lib2.recorder.micDenied')
        : i18n.t('lib2.recorder.micFailed'),
    });
    return;
  }

  // Run the mic through our 3 non-experimental filters: echo-cancellation
  // (above, browser-level), the proprietary "bunker" DSP, and RNNoise. The
  // experimental DeepFilterNet (`dfnet`) is intentionally excluded — it's the
  // heavy ML model and overkill for a short voice note. Record the PROCESSED
  // track when any stage is on; fall back to the raw stream otherwise.
  let recordStream = stream;
  try {
    audioGraph = await buildProcessedAudio(
      stream,
      { bunker: prefs.bunkerFilter, rnnoise: prefs.neuralSuppression, dfnet: false, gain: false },
      {},
    );
    if (audioGraph) {
      audioGraph.setEnabled(true);
      recordStream = new MediaStream([audioGraph.track]);
    }
  } catch {
    // Filter graph failed — record the raw mic rather than dropping the take.
    audioGraph = null;
    recordStream = stream;
  }

  chosenMime = pickMime();
  chunks = [];
  recorder = new MediaRecorder(recordStream, { mimeType: chosenMime });
  recorder.addEventListener('dataavailable', (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  });
  recorder.start(100); // collect chunks every 100ms — smoother stop()
  startedAt = Date.now();
  set({ kind: 'recording', startedAt, durationMs: 0 });
  tickHandle = setInterval(() => {
    if (current.kind !== 'recording') return;
    set({ kind: 'recording', startedAt, durationMs: Date.now() - startedAt });
  }, 100);
}

/**
 * Stop recording and return the resulting blob. If the recorder isn't
 * active, returns null (caller treats it as "nothing happened").
 */
export function stopRecording(): Promise<RecordingResult | null> {
  return new Promise((resolve) => {
    if (current.kind !== 'recording' || !recorder) {
      resolve(null);
      return;
    }
    const finalize = () => {
      const durationMs = Date.now() - startedAt;
      const blob = new Blob(chunks, { type: chosenMime });
      cleanup();
      set({ kind: 'idle' });
      resolve({ blob, mime: chosenMime, durationMs });
    };
    recorder.addEventListener('stop', finalize, { once: true });
    recorder.stop();
  });
}

/** Throw away the in-progress recording. */
export function cancelRecording(): void {
  if (current.kind !== 'recording' || !recorder) {
    cleanup();
    set({ kind: 'idle' });
    return;
  }
  try {
    recorder.stop();
  } catch {
    /* ignore */
  }
  chunks = [];
  cleanup();
  set({ kind: 'idle' });
}

export function clearRecorderError(): void {
  if (current.kind === 'error') set({ kind: 'idle' });
}

function cleanup() {
  if (tickHandle) clearInterval(tickHandle);
  tickHandle = null;
  recorder = null;
  if (audioGraph) {
    audioGraph.dispose();
    audioGraph = null;
  }
  if (stream) {
    for (const track of stream.getTracks()) track.stop();
    stream = null;
  }
  chunks = [];
}
